<script lang="ts">
import { useI18n } from 'vue-i18n';
import { QForm } from 'quasar';
import { computed, defineComponent, ref, watch } from 'vue';
import { useRoute } from 'vue-router';

import { useAuthUserStore } from '@/app/stores/user';
import { AccountStatementForm, INITIAL_ACCOUNT_STATEMENT_FORM, STATEMENT_TYPES } from './lib';
import { AccountsWithCurrency, findAccountsWithCurrencyWithPermission } from '@/shared/services/account';
import { AccountStatus, AccountType, PaymentType } from '@/app/graphql/account/sdk.account';

import EntityOrderStatement from '@/entities/order-statement';
import EntityInputDateWithRule from '@/entities/date-with-rule';
import EntityAccountWithCurrency from '@/entities/account/with-currency';
import UiBottomSheet from '@/shared/ui/bottom-sheet/index.vue';
import { useRangeDate } from '@/shared/lib/use-range-date';
import UiSelect from '@/shared/ui/select';

export default defineComponent({
  name: 'FeatureDocumentsAccountStatementOrder',
  components: {
    UiSelect,
    UiBottomSheet,
    EntityInputDateWithRule,
    EntityOrderStatement,
    EntityAccountWithCurrency,
  },
  emits: ['createAccountStatement'],

  setup() {
    const { t } = useI18n();
    const route = useRoute();
    const authUserStore = useAuthUserStore();
    const statementTypesOptions = [...STATEMENT_TYPES];
    const currentContract = computed(() => authUserStore.selectedUserContract);
    const isShowDialog = ref(false);
    const optionsLoading = ref(false);
    const optionsAccounts = ref<AccountsWithCurrency[]>([]);
    const form = ref<AccountStatementForm>({ ...INITIAL_ACCOUNT_STATEMENT_FORM });
    const formRef = ref<QForm>();
    const { toDay, week, month, yesterday } = useRangeDate();

    const setDate = (start: string, end: string) => {
      if (form.value.startDate === start && form.value.endDate === end) return;
      form.value.startDate = start;
      form.value.endDate = end;
    };
    const isActive = (start: string, end: string) => {
      if (form.value.startDate && form.value.endDate)
        return form.value.startDate === start && form.value.endDate === end;
    };
    const clear = () => {
      isShowDialog.value = false;
      form.value = { ...INITIAL_ACCOUNT_STATEMENT_FORM };
    };
    const getOptionsAccounts = async () => {
      optionsLoading.value = true;
      try {
        if (!currentContract.value?.orgReferenceId) return;

        const filterInput = {
          partyId: currentContract.value.orgReferenceId,
          statuses: [AccountStatus.Open, AccountStatus.Arrested],
          accountTypes: [AccountType.Rko],
        };
        optionsAccounts.value = await findAccountsWithCurrencyWithPermission(filterInput, [PaymentType.Read]);
      } catch (e: any) {
        console.error(e.message);
      } finally {
        optionsLoading.value = false;
      }
    };

    const setPrefilledAccount = () => {
      const accountNumber = route.query.account;
      const currencyCode = route.query.currency;

      const prefilledAccount = optionsAccounts.value.find((account) => account.account === accountNumber);

      if (prefilledAccount) {
        form.value.account = prefilledAccount;
        form.value.currencies = prefilledAccount.currency.filter((currency) => currency.isoCode === currencyCode);
        return;
      }

      if (optionsAccounts.value.length === 1) form.value.account = optionsAccounts.value[0];
    };

    const onAccountSelect = (account: AccountsWithCurrency) => {
      form.value.account = account;
      form.value.currencies = [];
    };

    watch(
      () => isShowDialog.value,
      () => {
        form.value.endDate = toDay;
        if (isShowDialog.value) return getOptionsAccounts().then(setPrefilledAccount);
        form.value = { ...INITIAL_ACCOUNT_STATEMENT_FORM };
      }
    );

    watch(
      () => form.value.startDate,
      () => formRef.value?.validate()
    );

    return {
      t,
      isShowDialog,
      form,
      statementTypesOptions,
      optionsAccounts,
      optionsLoading,
      formRef,
      week,
      month,
      toDay,
      yesterday,
      clear,
      setDate,
      isActive,
      onAccountSelect,
    };
  },
});
</script>

<template>
  <div class="order-statement">
    <q-btn no-caps flat class="ob-submit-btn full-width" @click="isShowDialog = true">
      <span>{{ t('DOCUMENTS.STATEMENT_ACCOUNT.ORDER.EXTRACT') }}</span>
    </q-btn>
  </div>

  <ui-bottom-sheet v-model="isShowDialog">
    <div class="text-h5 text-center ob-mb-md">
      {{ t('DOCUMENTS.STATEMENT_ACCOUNT.ORDER.OPTIONS') }}
    </div>
    <q-form ref="formRef" @submit="$emit('createAccountStatement', form)">
      <div class="ob-mb-md">
        <entity-account-with-currency
          :model-value="form.account"
          :label="t('SHARED.ACCOUNT_NUMBER')"
          class="o-input operation-input"
          clearable
          lazy-rules
          :loading="optionsLoading"
          :options="optionsAccounts"
          @update:model-value="onAccountSelect"
        />
      </div>
      <div v-if="form.account" class="ob-mb-md">
        <entity-order-statement
          v-model="form.currencies"
          :label="t('SHARED.CURRENCY')"
          class="o-input operation-input"
          clearable
          lazy-rules
          use-chips
          multiple
          :options="form.account.currency"
          :hint="t('DOCUMENTS.STATEMENT_ACCOUNT.ORDER.MULTIPLE_CURRENCY')"
        />
      </div>
      <ui-select
        v-model="form.statementType"
        map-options
        emit-value
        :label="t('DOCUMENTS.STATEMENT_ACCOUNT.ORDER.VIEW')"
        :options="statementTypesOptions"
        class="ob-mb-md"
      >
        <template #selected-item="{ opt }">{{ t(opt.label) }}</template>
        <template #option-body="opt">{{ t(opt.label) }}</template>
      </ui-select>
      <div class="ob-mb-md periods">
        <entity-input-date-with-rule
          v-model="form.startDate"
          :label="t('DOCUMENTS.STATEMENT_ACCOUNT.ORDER.DATE_FROM')"
          class="o-input mb"
          no-future
          required
        />
        <entity-input-date-with-rule
          v-model="form.endDate"
          :label="t('DOCUMENTS.STATEMENT_ACCOUNT.ORDER.DATE_TO')"
          class="o-input"
          no-future
          :after-days="form.startDate"
        />
      </div>
      <div class="flex justify-around ob-my-lg range-date text-muted">
        <span :class="{ active: isActive(toDay, toDay) }" @click="setDate(toDay, toDay)">
          {{ t('DOCUMENTS.STATEMENT_ACCOUNT.RANGE_DATE.TODAY') }}
        </span>
        <span :class="{ active: isActive(yesterday, yesterday) }" @click="setDate(yesterday, yesterday)">
          {{ t('DOCUMENTS.STATEMENT_ACCOUNT.RANGE_DATE.YESTERDAY') }}
        </span>
        <span :class="{ active: isActive(week, toDay) }" @click="setDate(week, toDay)">
          {{ t('DOCUMENTS.STATEMENT_ACCOUNT.RANGE_DATE.WEEK') }}
        </span>
        <span :class="{ active: isActive(month, toDay) }" @click="setDate(month, toDay)">
          {{ t('DOCUMENTS.STATEMENT_ACCOUNT.RANGE_DATE.MONTH') }}
        </span>
      </div>
      <q-btn type="submit" no-caps class="ob-submit-btn" :disable="!optionsAccounts.length">
        {{ t('DOCUMENTS.STATEMENT_ACCOUNT.ORDER.EXTRACT') }}
      </q-btn>
    </q-form>
  </ui-bottom-sheet>
</template>

<style scoped lang="scss">
@import 'styles.module';
</style>
